// ResetPassword.jsx — FULL
// Eye button uses EXACT same classes/structure as Login.jsx:
// - wrapper: sr-login__passwordWrap
// - input:   sr-login__input
// - eye btn: sr-login__eye (+ is-active)
//
// Requires backend endpoint:
// POST /authen/reset-password-validate/ { uid, token }

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/http";
import "../../styles/login/Forgot.css"; // pagina reset (card, layout, etc.)
import "../../styles/login/Login.css";  // pentru sr-login__passwordWrap / sr-login__input / sr-login__eye

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { uid, token } = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return { uid: sp.get("uid") || "", token: sp.get("token") || "" };
  }, [location.search]);

  const isConfirmMode = Boolean(uid && token);

  // request mode
  const [email, setEmail] = useState("");

  // confirm mode
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  // 👁 toggles
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI state
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // validate link on mount
  const [checkingLink, setCheckingLink] = useState(false);
  const [linkValid, setLinkValid] = useState(false);

  const parseApiError = (err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || "A apărut o eroare.";

    if (typeof data === "string") return data;
    if (Array.isArray(data)) return data[0] || "A apărut o eroare.";
    if (data.detail) return data.detail;

    if (typeof data === "object") {
      const k = Object.keys(data)[0];
      const v = data[k];
      if (Array.isArray(v)) return v[0];
      if (typeof v === "string") return v;
    }
    return "A apărut o eroare.";
  };

  // ✅ auto-hide message/error după 5 secunde
  useEffect(() => {
    if (!message && !error) return;
    const t = setTimeout(() => {
      setMessage("");
      setError("");
    }, 5000);
    return () => clearTimeout(t);
  }, [message, error]);

  // 1) Check token validity immediately when opening link
  useEffect(() => {
    if (!isConfirmMode) return;

    let alive = true;
    setMessage("");
    setError("");
    setLinkValid(false);
    setCheckingLink(true);

    (async () => {
      try {
        await api.post("/authen/reset-password-validate/", { uid, token });
        if (!alive) return;
        setLinkValid(true);
      } catch (err) {
        if (!alive) return;
        const msg = parseApiError(err) || "Link invalid/expirat.";
        setError(msg);

        // redirect quickly to request page
        setTimeout(() => {
          navigate("/reset-password", { replace: true });
        }, 900);
      } finally {
        if (!alive) return;
        setCheckingLink(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [isConfirmMode, uid, token, navigate]);

  const handleRequest = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError("Email este obligatoriu.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/authen/reset-password/", { email: cleanEmail });
      setMessage(res?.data?.detail || "Dacă email-ul există, a fost trimis un link.");
      setEmail("");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!uid || !token) {
      setError("Link invalid. Cere un nou link de resetare.");
      navigate("/reset-password", { replace: true });
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError("Parolele nu coincid.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/authen/reset-password-confirm/", {
        uid,
        token,
        new_password: newPassword,
        confirm_new_password: confirmNewPassword,
      });

      setMessage(res?.data?.detail || "Password has been reset successfully.");
      setNewPassword("");
      setConfirmNewPassword("");
      setShowNewPassword(false);
      setShowConfirmPassword(false);

      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Prevent form flash while validating token
  if (isConfirmMode && checkingLink) {
    return (
      <div className="srReset_page">
        <div className="srReset_card">
          <h2 className="srReset_title">Verificăm linkul…</h2>
          <p className="srReset_muted">Te rugăm așteaptă.</p>
          {error && <div className="srReset_msg srReset_msgErr">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="srReset_page">
      <div className="srReset_card">
        <h2 className="srReset_title">
          {isConfirmMode ? "Set new password" : "Password Reset"}
        </h2>

        {!isConfirmMode ? (
          // ========== REQUEST FORM ==========
          <form onSubmit={handleRequest} className="srReset_form">
            <div className="srReset_field">
              <label htmlFor="email">Enter email</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="srReset_btnPrimary" disabled={submitting}>
              {submitting ? "Sending..." : "Send reset link"}
            </button>

            <div className="srReset_hint">
              <span>Remembered your password? </span>
              <button type="button" className="srReset_linkBtn" onClick={() => navigate("/login")}>
                Back to login
              </button>
            </div>
          </form>
        ) : !linkValid ? (
          // ========== INVALID/EXPIRED (will redirect) ==========
          <div>
            <p className="srReset_muted">Link invalid sau expirat. Te redirecționăm…</p>
            {error && <div className="srReset_msg srReset_msgErr">{error}</div>}
          </div>
        ) : (
          // ========== CONFIRM FORM (valid link) ==========
          <form onSubmit={handleConfirm} className="srReset_form">
            {/* New password (same eye classes as Login.jsx) */}
            <div className="srReset_field">
              <label htmlFor="new_password">New password</label>
              <div className="sr-login__passwordWrap">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="new_password"
                  name="new_password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="sr-login__input"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className={["sr-login__eye", showNewPassword ? "is-active" : ""].join(" ")}
                  onClick={() => setShowNewPassword((v) => !v)}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  👁
                </button>
              </div>
            </div>

            {/* Confirm password (same eye classes as Login.jsx) */}
            <div className="srReset_field">
              <label htmlFor="confirm_new_password">Confirm new password</label>
              <div className="sr-login__passwordWrap">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirm_new_password"
                  name="confirm_new_password"
                  placeholder="Confirm new password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="sr-login__input"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className={["sr-login__eye", showConfirmPassword ? "is-active" : ""].join(" ")}
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  👁
                </button>
              </div>
            </div>

            <button type="submit" className="srReset_btnPrimary" disabled={submitting}>
              {submitting ? "Saving..." : "Confirm"}
            </button>

            <div className="srReset_hint">
              <button type="button" className="srReset_linkBtn" onClick={() => navigate("/login")}>
                Back to login
              </button>
            </div>
          </form>
        )}

        {message && <div className="srReset_msg srReset_msgOk">{message}</div>}
        {(!isConfirmMode || linkValid) && error && (
          <div className="srReset_msg srReset_msgErr">{error}</div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;